import React from 'react';
import { motion } from 'framer-motion';
import { useMotionPreference } from '../../../hooks/useMotionPreference';
interface FilamentProps {
  id: string;
  /** Anna's core */
  x1: number;
  y1: number;
  /** Where the card is attached */
  x2: number;
  y2: number;
  color: string;
  /** Increments when the card's data updates — a pulse travels from Anna to the card */
  ping: number;
}
const EASE = [0.23, 1, 0.32, 1] as const;

/** Thin luminous line from Anna's core to a summoned card. Draws out with the card and retracts with it. */
export function Filament({
  id,
  x1,
  y1,
  x2,
  y2,
  color,
  ping
}: FilamentProps) {
  const reduce = useMotionPreference();
  const dx = x2 - x1;
  const d = `M ${x1} ${y1} C ${x1 + dx * 0.45} ${y1} ${x1 + dx * 0.7} ${y2} ${x2} ${y2}`;
  const gid = `filament-${id}`;
  const draw = reduce ? {
    duration: 0.2
  } : {
    duration: 0.32,
    ease: EASE
  };
  return <motion.g initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0,
    transition: {
      duration: 0.28,
      ease: EASE
    }
  }} transition={{
    duration: 0.12
  }} data-id="element-1642" data-mp="phq4ref7ulof">
      <defs data-id="element-1643" data-mp="3ov9rj0aecujz">
        <linearGradient id={gid} gradientUnits="userSpaceOnUse" x1={x1} y1={y1} x2={x2} y2={y2} data-id="element-1644" data-mp="3suik41tpamdy">
          <stop offset="0" style={{
          stopColor: 'rgb(var(--accent))',
          stopOpacity: 0.95
        }} data-id="element-1645" data-mp="1o7oil7gmdu0" />
          <stop offset="1" style={{
          stopColor: color,
          stopOpacity: 0.9
        }} data-id="element-1646" data-mp="1meeqx1jvdhs" />
        </linearGradient>
      </defs>
      <motion.path fill="none" stroke={`url(#${gid})`} strokeWidth={7} strokeOpacity={0.12} strokeLinecap="round" initial={{
      d,
      pathLength: reduce ? 1 : 0
    }} animate={{
      d,
      pathLength: 1
    }} exit={{
      pathLength: 0
    }} transition={draw} data-id="element-1647" data-mp="3557rx8qf20bf" />
      <motion.path fill="none" stroke={`url(#${gid})`} strokeWidth={1.5} strokeOpacity={0.8} strokeLinecap="round" initial={{
      d,
      pathLength: reduce ? 1 : 0
    }} animate={{
      d,
      pathLength: 1
    }} exit={{
      pathLength: 0
    }} transition={draw} data-id="element-1648" data-mp="3juccuxoliin8" />
      {ping > 0 && (reduce ? <motion.path key={ping} d={d} fill="none" stroke={color} strokeWidth={3} initial={{
      opacity: 0.9
    }} animate={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} data-id="element-1649" data-mp="2jaqzrv80lwlx" /> : <motion.path key={ping} d={d} fill="none" stroke="#FFFFFF" strokeWidth={3} strokeLinecap="round" initial={{
      pathLength: 0.12,
      pathOffset: 0,
      opacity: 1
    }} animate={{
      pathOffset: 0.88,
      opacity: [1, 1, 0]
    }} transition={{
      duration: 0.65,
      ease: EASE
    }} data-id="element-1650" data-mp="2t256amydvwok" />)}
      <motion.circle r={4} fill={color} initial={{
      cx: x2,
      cy: y2,
      opacity: 0
    }} animate={{
      cx: x2,
      cy: y2,
      opacity: 1
    }} transition={{
      duration: 0.2,
      opacity: {
        delay: reduce ? 0 : 0.25,
        duration: 0.15
      }
    }} data-id="element-1651" data-mp="2acygyh3346ts" />
    </motion.g>;
}